/*
 *   File : orders.js
 *   Author : https://evoqins.com
 *   Description : orders component (API integrated)
 *   Integrations : orders/filter-data, orders/list
 *   Version : 1.1.0
 */

import React, { useState, useEffect, useRef } from "react";
import { DataTableComponent } from "../../../Components/DataTable";
import { Chip, EmptyScreen, Loader } from "../../../Components/Other";
import { PrimaryButton } from "../../../Components/Buttons";
import { useNavigate } from "react-router-dom";
import { APIService } from "../../../Service";
import { toast } from "react-toastify";

export default function Orders({
  id,
  setFilterData,
  setFilterType,
  appliedFilters,
}) {
  const navigate = useNavigate();

  const [loader, setLoader] = useState(false); // ← false, not true
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [selectedRow, setSelectedRow] = useState({ selectedRows: [] });
  const [isAllSelected, setIsAllSelected] = useState(false);

  const listFetchIdRef = useRef(0);

  // Fetch filter data and push to parent
  useEffect(() => {
    setFilterType(1);
    _getFilterData();
  }, []);

  // Re-fetch orders whenever filters, page, perPage, or id changes
  useEffect(() => {
    if (id === undefined || id === null) {
      setLoader(false); // ← prevent infinite spinner when id isn't ready
      return;
    }
    _getOrders();
  }, [appliedFilters, page, perPage, id]);

  // Select All covers every page of the current list, so only a different list clears it
  useEffect(() => {
    setIsAllSelected(false);
  }, [appliedFilters, perPage, id]);

  // Select All covers every page of the current list, so only a different list clears it
  useEffect(() => {
    setIsAllSelected(false);
  }, [appliedFilters, perPage, id]);

  const _handleSelectedRowsChange = (state) => {
    setSelectedRow(state);
    setIsAllSelected(state.allSelected);
  };

  const COLUMNS = [
    {
      name: "Date",
      selector: (row) => row.order_date,
      sortable: true,
      width: "160px",
    },
    {
      name: "Order ID",
      selector: (row) => row.order_display_id,
      sortable: true,
      width: "200px",
    },
    {
      name: "Order Type",
      sortable:true,
      selector: (row) => row.investment_type,
      width: "160px",
    },
    {
      name: "Type",
      selector: (row) => row.type,
      width: "140px",
      sortable:true,
      cell: (row) => (
        <Chip
          text={row.type}
          style="px-2 py-1 bg-lavender-blue bg-opacity-43"
          textClr="text-facebook-blue"
        />
      ),
    },
    {
      name: "Quantity",
      sortable:true,
      selector: (row) => row.order_count,
      width: "120px",
    },
    {
      name: "Amount",
      sortable:true,
      selector: (row) => row.amount_text,
      width: "140px",
    },
    {
      name: "Order Status",
      sortable:true,
      selector: (row) => row.order_status,
      width: "160px",
      cell: (row) => (
        <p
          className={`${
            row.order_status === "Success"
              ? "text-medium-sea-green"
              : "text-copper-red"
          } mb-0`}
        >
          {row.order_status}
        </p>
      ),
    },
    {
      name: "Payment Status",
      sortable:true,
      selector: (row) => row.payment_status,
      width: "180px",
      cell: (row) => (
        <p
          className={`${
            row.payment_status === "Success"
              ? "text-medium-sea-green"
              : "text-copper-red"
          } mb-0`}
        >
          {row.payment_status}
        </p>
      ),
    },
    {
      name: "Action",
      cell: (row) => (
        <PrimaryButton
          ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue"
          name="View Order"
          onPress={() =>
            navigate(
              `/orders/detail/${row.investment_type?.toLowerCase() || "order"}/${row.order_id}`
            )
          }
        />
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  // Fetch filter options from API and forward to parent via setFilterData
  const _getFilterData = () => {
    APIService(false, "orders/filter-data")
      .then((res) => {
        if (!res) return;
        const raw = res.data ?? res.filters ?? res.orders ?? res;
        const list = Array.isArray(raw)
          ? raw
          : (raw?.filters ??
              raw?.data ??
              raw?.items ??
              raw?.filter_options ??
              raw?.orders ??
              []) || [];
        setFilterData(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error("Failed to load orders filter data:", err);
        setFilterData([]);
      });
  };

  // Fetch paginated orders list from API
  const _getOrders = () => {
    const fetchId = ++listFetchIdRef.current;
    setLoader(true);

    const payload = {
      customer_id: parseInt(id),
      order_status: appliedFilters?.order_status,
      payment_status: appliedFilters?.payment_status,
      type: null,
      investment_type: appliedFilters?.investment_type ?? null,
      page_num: page,
      page_size: perPage,
    };

    APIService(true, "orders/list", payload)
      .then((res) => {
        if (fetchId !== listFetchIdRef.current) return;
        if (res.status_code === 200) {
          const orders = res.data?.orders || [];
          setTableData(orders);
          setTotalRows(res.data?.total_count || orders.length);
          setIsEmpty(orders.length === 0);
        } else {
          setTableData([]);
          setIsEmpty(true);
        }
      })
      .catch((err) => {
        if (fetchId !== listFetchIdRef.current) return;
        console.error("Orders list error:", err);
        setTableData([]);
        setIsEmpty(true);
      })
      .finally(() => {
        if (fetchId === listFetchIdRef.current) {
          setLoader(false);
        }
      });
  };


  return (
    <div className="pt-1 row-gap-3 d-flex flex-column">
      {/* Table */}
      {loader ? (
        <Loader />
      ) : isEmpty ? (
        <EmptyScreen message={"No Orders Found"} />
      ) : (
        <DataTableComponent
          info={true}
          columns={COLUMNS}
          data={tableData}
          pagination={true}
          paginationServer={true}
          selectableRows={true}
          allSelected={isAllSelected}
          currentPage={page}
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          onChangePage={(p) => setPage(p)}
          onChangeRowsPerPage={(newPerPage) => {
            setPerPage(newPerPage);
            setPage(1);
          }}
          onSelectedRowsChange={(state) => {
            setSelectedRow(state);
            setIsAllSelected(state.allSelected);
          }}
          className="e-table-basic"
        />
      )}
    </div>
  );
}